import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { ItcStatus, payablesService, VendorBillInput, VendorBillQueryParams, VendorPaymentInput } from '../services/payables.service';

export const useVendorBills = (params: VendorBillQueryParams, enabled = true) =>
  useQuery({ queryKey: ['payables', 'bills', params], queryFn: () => payablesService.getBills(params), enabled, placeholderData: keepPreviousData });

export const useVendorBillSummary = () => useQuery({ queryKey: ['payables', 'billSummary'], queryFn: () => payablesService.getBillSummary() });

export const useBillableLines = (vendorId: string | undefined) =>
  useQuery({ queryKey: ['payables', 'billable', vendorId], queryFn: () => payablesService.getBillable(vendorId as string), enabled: !!vendorId });

export const useVendorBill = (id: string | undefined) =>
  useQuery({ queryKey: ['payables', 'bill', id], queryFn: () => payablesService.getBill(id as string), enabled: !!id });

export const useVendorPayments = (params: { page?: number; limit?: number; search?: string; status?: string }, enabled = true) =>
  useQuery({ queryKey: ['payables', 'payments', params], queryFn: () => payablesService.getPayments(params), enabled, placeholderData: keepPreviousData });

export const useVendorPaymentSummary = () => useQuery({ queryKey: ['payables', 'paymentSummary'], queryFn: () => payablesService.getPaymentSummary() });

export const usePayablesAgeing = (enabled = true) => useQuery({ queryKey: ['payables', 'ageing'], queryFn: () => payablesService.getAgeing(), enabled });

export const useVendorPayment = (id: string | undefined) =>
  useQuery({ queryKey: ['payables', 'payment', id], queryFn: () => payablesService.getPayment(id as string), enabled: !!id });

// Bills and payments change what is owed and what goods receipts can still be billed
const useInvalidatePayables = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['payables'] });
    queryClient.invalidateQueries({ queryKey: ['goodsReceipts'] });
  };
};

export const useCreateVendorBill = () => {
  const invalidate = useInvalidatePayables();
  return useMutation({ mutationFn: (data: VendorBillInput) => payablesService.createBill(data), onSuccess: invalidate });
};

export const useSetItcStatus = () => {
  const invalidate = useInvalidatePayables();
  return useMutation({ mutationFn: ({ id, itcStatus }: { id: string; itcStatus: ItcStatus }) => payablesService.setItc(id, itcStatus), onSuccess: invalidate });
};

export const useCancelVendorBill = () => {
  const invalidate = useInvalidatePayables();
  return useMutation({ mutationFn: ({ id, reason }: { id: string; reason: string }) => payablesService.cancelBill(id, reason), onSuccess: invalidate });
};

export const useCreateVendorPayment = () => {
  const invalidate = useInvalidatePayables();
  return useMutation({ mutationFn: (data: VendorPaymentInput) => payablesService.createPayment(data), onSuccess: invalidate });
};

export const useAllocateVendorPayment = () => {
  const invalidate = useInvalidatePayables();
  return useMutation({
    mutationFn: ({ id, allocations }: { id: string; allocations: { vendorBillId: string; amount: number }[] }) => payablesService.allocatePayment(id, allocations),
    onSuccess: invalidate,
  });
};

export const useCancelVendorPayment = () => {
  const invalidate = useInvalidatePayables();
  return useMutation({ mutationFn: ({ id, reason }: { id: string; reason: string }) => payablesService.cancelPayment(id, reason), onSuccess: invalidate });
};
